import { ArrowLeft } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { useT, type StringId } from '../lib/i18n'
import { OverlayContext } from '../ui/OverlayContext'
import { PhoneFrame } from '../ui/PhoneFrame'

interface PushedProps {
  titleKey: StringId
  children?: ReactNode
}

/**
 * A full-screen route: a *sibling* of the tab tree, not a child of it. That is
 * what "pushing over the shell" means in React Router — the shell simply is not
 * in this subtree, so the tab bar is absent rather than covered.
 *
 * It reuses `PhoneFrame` and carries **its own overlay root**. A review caught
 * the version that did not: `useOverlayRoot()` returned null on every pushed
 * route, so a sheet opened from the Log screen — which is the single most
 * likely place to open one — would have rendered nothing at all, silently.
 *
 * There is no tab bar below to reserve the inset, so `<main>` pads by the real
 * `--suap-safe-b` from `:root`.
 *
 * SUAP-0104.
 */
export function Pushed({ titleKey, children }: PushedProps) {
  const [overlayRoot, setOverlayRoot] = useState<HTMLElement | null>(null)
  const navigate = useNavigate()
  const { t } = useT()

  return (
    <PhoneFrame>
      <OverlayContext.Provider value={overlayRoot}>
        <header className="flex shrink-0 items-center gap-2 border-b border-hairline bg-surface p-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label={t('action.back')}
            // The focus ring is explicit rather than the browser default: the UA
            // ring is 1px in its own blue, which is neither a token nor the 2px
            // the rest of the kit draws. Found by `check:a11y` in SUAP-0103 —
            // a change to a SUAP-0104 file, recorded rather than made quietly.
            className="flex h-11 w-11 items-center justify-center rounded-chip text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
          >
            <ArrowLeft aria-hidden size={22} />
          </button>
          <h1 className="text-lg font-semibold text-ink">{t(titleKey)}</h1>
        </header>

        <main
          className="min-h-0 flex-1 overflow-y-auto p-4"
          style={{ paddingBottom: 'calc(16px + var(--suap-safe-b))' }}
        >
          {children ?? <p className="text-ink-soft">{t('state.comingSoon')}</p>}
        </main>

        <div ref={setOverlayRoot} id="overlay-root" className="absolute inset-0 empty:hidden" />
      </OverlayContext.Provider>
    </PhoneFrame>
  )
}
